/*
 * zodiac-builder.js — Starmap / Zodiac charm personalizer controller.
 *
 * Exposes window.ZodiacBuilderUI.mount(rootEl) which wires up a builder UI block
 * (sign grid + Name/Date inputs + preset color rail + custom colors) and drives
 * the shared live preview (window.ZodiacPreview). Returns a small controller the
 * caller uses to read/validate state.
 *
 * Two consumers:
 *   - PDP: this file auto-initializes when window.__zodiacBuilder is present,
 *     mounts on #zodiac-builder, hides the native ATC, and POSTs to /cart/add.js.
 *   - Build-your-charm bundle: assets/1r-theme.js opens #starmap-modal, calls
 *     ZodiacBuilderUI.mount(modalRoot) and reads getState() on confirm.
 */
(function () {
  'use strict';

  if (window.ZodiacBuilderUI) return;

  function cfg() { return window.__zodiacConfig || {}; }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function labelFor(sign) {
    var labels = cfg().labels || {};
    if (labels[sign]) return labels[sign];
    return sign ? sign.charAt(0).toUpperCase() + sign.slice(1) : '';
  }

  function datesFor(sign) {
    var dates = cfg().dates || {};
    return dates[sign] || '';
  }

  function buildSignGrid(container) {
    if (!container || container.children.length) return; // already built (idempotent)
    var c = cfg();
    var urls = c.signSvgUrls || {};
    var starUrls = c.signStarUrls || {};
    var signs = (window.ZodiacPreview && window.ZodiacPreview.SIGNS) || Object.keys(urls);
    container.setAttribute('role', 'radiogroup');
    for (var i = 0; i < signs.length; i++) {
      var sign = signs[i];
      if (!urls[sign]) continue;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'zodiac-sign-tile';
      btn.setAttribute('data-sign', sign);
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.tabIndex = -1;
      var img = document.createElement('img');
      img.src = starUrls[sign] || urls[sign]; // constellation-only thumbnail
      img.alt = '';
      img.loading = 'lazy';
      var span = document.createElement('span');
      span.textContent = labelFor(sign);
      btn.appendChild(img);
      btn.appendChild(span);
      var dates = datesFor(sign);
      if (dates) {
        var dateSpan = document.createElement('span');
        dateSpan.className = 'zodiac-sign-tile__dates';
        dateSpan.textContent = dates;
        btn.appendChild(dateSpan);
      }
      container.appendChild(btn);
    }
  }

  function buildPresets(container, presets) {
    if (!container || container.children.length) return; // already built (idempotent)
    container.setAttribute('role', 'radiogroup');
    for (var i = 0; i < presets.length; i++) {
      var pr = presets[i];
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'zodiac-preset';
      btn.setAttribute('data-c1', pr.c1);
      btn.setAttribute('data-c2', pr.c2);
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.setAttribute('aria-label', pr.name || (pr.c1 + ' and ' + pr.c2));
      btn.title = pr.name || '';
      btn.style.background = 'linear-gradient(135deg, ' + pr.c1 + ', ' + pr.c1 + ' 50%, ' + pr.c2 + ' 50%, ' + pr.c2 + ')';
      container.appendChild(btn);
    }
  }

  /**
   * mount(root, opts) -> controller
   * controller: { getState, isComplete, validate, reset, onChange, render, root }
   */
  function mount(root, opts) {
    opts = opts || {};
    if (!root || root.__zodiacMounted) {
      return root && root.__zodiacController ? root.__zodiacController : null;
    }
    if (window.ZodiacPreview) window.ZodiacPreview.configure(cfg().signSvgUrls);

    var presets = cfg().presets || [];
    var maxName = cfg().maxNameLength || 20;

    var signGrid = root.querySelector('.zodiac-sign-grid');
    var colorsRail = root.querySelector('.zodiac-colors-rail');

    // Build sign tiles + preset swatches from config (single source of truth).
    buildSignGrid(signGrid);
    buildPresets(colorsRail, presets);
    // Inline the custom-colors toggle after the last swatch; flex-wrap drops it to its
    // own line only when there isn't room on the swatch row (dynamic, no measuring).
    var inlineToggle = root.querySelector('.zodiac-custom-toggle');
    if (colorsRail && inlineToggle) colorsRail.appendChild(inlineToggle);

    var els = {
      preview: root.querySelector('.zodiac-preview'),
      tiles: root.querySelectorAll('.zodiac-sign-tile'),
      name: root.querySelector('.zodiac-name'),
      date: root.querySelector('.zodiac-date'),
      presets: root.querySelectorAll('.zodiac-preset'),
      customToggle: root.querySelector('.zodiac-custom-toggle'),
      custom: root.querySelector('.zodiac-custom'),
      color1: root.querySelector('.zodiac-color1'),
      color2: root.querySelector('.zodiac-color2'),
      error: root.querySelector('.zodiac-error'),
      nameCount: root.querySelector('.zodiac-name-count')
    };

    var defaultPreset = presets[0] || { c1: '#7b4dff', c2: '#ff5ea8' };
    var state = {
      sign: opts.defaultSign || (els.tiles[0] && els.tiles[0].getAttribute('data-sign')) || 'aries',
      name: '',
      date: '',
      color1: defaultPreset.c1,
      color2: defaultPreset.c2
    };

    var changeCbs = [];
    function emitChange() { for (var i = 0; i < changeCbs.length; i++) { try { changeCbs[i](getState()); } catch (e) {} } }

    function getState() {
      return {
        sign: state.sign,
        signLabel: labelFor(state.sign),
        name: state.name.trim(),
        date: state.date.trim(),
        color1: state.color1,
        color2: state.color2
      };
    }

    function isComplete() {
      var s = getState();
      return !!(s.sign && s.name && s.date && s.color1 && s.color2);
    }

    function doRender() {
      if (window.ZodiacPreview && els.preview) window.ZodiacPreview.render(els.preview, state);
    }
    var debouncedRender = debounce(doRender, 120);

    function update(immediate) {
      if (immediate) doRender(); else debouncedRender();
      emitChange();
    }

    // ── Sign grid ──
    function selectSign(sign) {
      state.sign = sign;
      for (var i = 0; i < els.tiles.length; i++) {
        var active = els.tiles[i].getAttribute('data-sign') === sign;
        els.tiles[i].classList.toggle('is-selected', active);
        els.tiles[i].setAttribute('aria-checked', active ? 'true' : 'false');
        els.tiles[i].tabIndex = active ? 0 : -1;
      }
      // Preload neighbours opportunistically.
      if (window.ZodiacPreview) window.ZodiacPreview.preload(sign);
      update(true);
    }

    for (var t = 0; t < els.tiles.length; t++) {
      (function (tile) {
        tile.addEventListener('click', function () { selectSign(tile.getAttribute('data-sign')); });
        tile.addEventListener('keydown', function (e) {
          var tiles = els.tiles, idx = Array.prototype.indexOf.call(tiles, tile), next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tiles[(idx + 1) % tiles.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tiles[(idx - 1 + tiles.length) % tiles.length];
          else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); selectSign(tile.getAttribute('data-sign')); return; }
          if (next) { e.preventDefault(); next.focus(); selectSign(next.getAttribute('data-sign')); }
        });
      })(els.tiles[t]);
    }

    // ── Name / Date ──
    if (els.name) {
      els.name.maxLength = maxName;
      els.name.addEventListener('input', function () {
        state.name = els.name.value;
        if (els.nameCount) els.nameCount.textContent = (maxName - els.name.value.length) + ' characters remaining';
        clearError();
        update(false);
      });
    }
    if (els.date) {
      els.date.addEventListener('input', function () {
        state.date = els.date.value;
        clearError();
        update(false);
      });
    }

    // ── Color presets ──
    function selectPreset(c1, c2, el) {
      state.color1 = c1;
      state.color2 = c2;
      for (var i = 0; i < els.presets.length; i++) {
        var active = els.presets[i] === el;
        els.presets[i].classList.toggle('is-selected', active);
        els.presets[i].setAttribute('aria-checked', active ? 'true' : 'false');
      }
      if (els.color1) els.color1.value = c1;
      if (els.color2) els.color2.value = c2;
      update(true);
    }
    for (var p = 0; p < els.presets.length; p++) {
      (function (sw) {
        sw.addEventListener('click', function () {
          selectPreset(sw.getAttribute('data-c1'), sw.getAttribute('data-c2'), sw);
        });
      })(els.presets[p]);
    }

    // ── Custom colors ──
    if (els.customToggle && els.custom) {
      els.customToggle.addEventListener('click', function () {
        var open = els.custom.classList.toggle('is-open');
        els.customToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    function onCustomColor() {
      // Deselect presets when a custom color is chosen.
      for (var i = 0; i < els.presets.length; i++) {
        els.presets[i].classList.remove('is-selected');
        els.presets[i].setAttribute('aria-checked', 'false');
      }
      state.color1 = els.color1 ? els.color1.value : state.color1;
      state.color2 = els.color2 ? els.color2.value : state.color2;
      update(true);
    }
    if (els.color1) els.color1.addEventListener('input', onCustomColor);
    if (els.color2) els.color2.addEventListener('input', onCustomColor);

    // ── Preview modal (opened by the .zodiac-preview-toggle button) ──
    els.previewToggle = root.querySelector('.zodiac-preview-toggle');
    els.previewModal = root.querySelector('.zodiac-preview-modal');
    els.previewClose = els.previewModal ? els.previewModal.querySelector('.zodiac-preview-modal__close') : null;
    els.previewBackdrop = els.previewModal ? els.previewModal.querySelector('[data-zodiac-close]') : null;
    // Relocate the overlay to <body> so position:fixed covers the viewport and isn't
    // trapped by an ancestor transform (e.g. the bundle's #starmap-modal).
    if (els.previewModal && els.previewModal.parentNode !== document.body) {
      document.body.appendChild(els.previewModal);
    }
    function openPreview() {
      doRender();
      if (els.previewModal) {
        els.previewModal.classList.add('is-open');
        els.previewModal.setAttribute('aria-hidden', 'false');
      }
    }
    function closePreview() {
      if (els.previewModal) {
        els.previewModal.classList.remove('is-open');
        els.previewModal.setAttribute('aria-hidden', 'true');
      }
    }
    // "Add to Cart" / "Go Back" inside the preview overlay.
    els.previewAtc = els.previewModal ? els.previewModal.querySelector('.zodiac-preview-atc') : null;
    els.previewBack = els.previewModal ? els.previewModal.querySelector('.zodiac-preview-back') : null;
    // The real ATC for this context: the PDP button or the bundle's confirm button.
    var contextAtc = root.querySelector('.zodiac-add-to-cart, #confirm-starmap-button');
    if (els.previewToggle) els.previewToggle.addEventListener('click', openPreview);
    if (els.previewClose) els.previewClose.addEventListener('click', closePreview);
    if (els.previewBackdrop) els.previewBackdrop.addEventListener('click', closePreview);
    if (els.previewBack) els.previewBack.addEventListener('click', closePreview);
    if (els.previewAtc) els.previewAtc.addEventListener('click', function () {
      closePreview();
      if (contextAtc) contextAtc.click();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && els.previewModal && els.previewModal.classList.contains('is-open')) closePreview();
    });

    // ── Errors ──
    function clearError() { if (els.error) { els.error.textContent = ''; els.error.style.display = 'none'; } }
    function validate() {
      var s = getState();
      var msg = '';
      if (!s.sign) msg = 'Please choose a zodiac sign.';
      else if (!s.name) msg = 'Please enter a name.';
      else if (!s.date) msg = 'Please enter a date.';
      else if (!s.color1 || !s.color2) msg = 'Please choose your colors.';
      if (msg && els.error) { els.error.textContent = msg; els.error.style.display = 'block'; }
      return !msg;
    }

    function reset() {
      state.name = ''; state.date = '';
      if (els.name) els.name.value = '';
      if (els.date) els.date.value = '';
      selectPreset(defaultPreset.c1, defaultPreset.c2, els.presets[0]);
    }

    // Initial paint.
    if (els.color1) els.color1.value = state.color1;
    if (els.color2) els.color2.value = state.color2;
    selectSign(state.sign);
    if (els.presets[0]) {
      els.presets[0].classList.add('is-selected');
      els.presets[0].setAttribute('aria-checked', 'true');
    }
    if (window.ZodiacPreview) window.ZodiacPreview.ensureFont().then(doRender);

    var controller = {
      root: root,
      getState: getState,
      isComplete: isComplete,
      validate: validate,
      reset: reset,
      render: doRender,
      onChange: function (cb) { if (typeof cb === 'function') changeCbs.push(cb); }
    };
    root.__zodiacMounted = true;
    root.__zodiacController = controller;
    return controller;
  }

  window.ZodiacBuilderUI = { mount: mount, labelFor: labelFor };

  // ── PDP auto-init ──
  function initPdp() {
    var conf = window.__zodiacBuilder;
    if (!conf) return;
    var root = document.getElementById('zodiac-builder');
    if (!root) return;

    var ctrl = mount(root, { defaultSign: conf.defaultSign });
    if (!ctrl) return;

    // Hide the native ATC / quantity / dynamic checkout — the builder owns ATC.
    var form = root.closest('.product-single__form') || document.querySelector('.product-single__form');
    if (form) {
      var nativeAtc = form.querySelector('.add-to-cart-btn');
      if (nativeAtc) nativeAtc.style.display = 'none';
      var qty = form.querySelector('.product__quantity');
      if (qty) qty.style.display = 'none';
      var pay = form.querySelector('.payment-buttons .shopify-payment-button');
      if (pay) pay.style.display = 'none';
    }

    var btn = root.querySelector('.zodiac-add-to-cart');
    var btnText = root.querySelector('.zodiac-add-to-cart-text');
    if (!btn) return;

    function variantId() {
      var sel = (form && form.querySelector('select[name="id"]')) || document.querySelector('select[name="id"]');
      if (sel && sel.value) return sel.value;
      return conf.variantId;
    }

    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      if (!ctrl.validate()) return;
      var s = ctrl.getState();
      btn.disabled = true;
      if (btnText) btnText.textContent = 'Adding...';

      var properties = {
        'Zodiac Sign': s.signLabel,
        'Name': s.name,
        'Date': s.date,
        '_sign_key': s.sign,
        '_color_1': s.color1,
        '_color_2': s.color2,
        '_starmapcharm': 'true'
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId(), quantity: 1, properties: properties }] })
      })
        .then(function (res) { if (!res.ok) throw new Error('add to cart failed'); return res.json(); })
        .then(function () {
          if (btnText) btnText.textContent = 'Added!';
          if (typeof jQuery !== 'undefined') jQuery(document.body).trigger('added.ajaxProduct');
          else if (typeof $ !== 'undefined') $(document.body).trigger('added.ajaxProduct');
          setTimeout(function () { if (btnText) btnText.textContent = 'Add to Cart'; btn.disabled = false; }, 1500);
        })
        .catch(function (err) {
          if (window.console) console.error('Starmap add to cart error:', err);
          if (btnText) btnText.textContent = 'Error — try again';
          btn.disabled = false;
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPdp);
  } else {
    initPdp();
  }
})();
